import React, { useState, useEffect } from 'react';
import { Calculator, Users, CheckSquare, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const CustomerSurveyResponseRateCalculator = () => {
  const [surveysSent, setSurveysSent] = useState(1000);
  const [surveysCompleted, setSurveysCompleted] = useState(150);
  const [responseRate, setResponseRate] = useState(0);

  useEffect(() => {
    calculateResponseRate();
  }, [surveysSent, surveysCompleted]);

  const calculateResponseRate = () => {
    const rate = (surveysCompleted / surveysSent) * 100;
    setResponseRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/customer-survey-response-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Survey Response Rate Calculator: Optimize Survey Performance | Free Tool</title>
        <meta name="description" content="Calculate your Customer Survey Response Rate instantly. Get industry benchmarks, optimization tips, and strategies to improve survey completion rates." />
        <meta name="keywords" content="survey response rate calculator, survey metrics, customer feedback analytics, survey optimization, survey completion rate" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Survey Response Rate Calculator: Boost Survey Performance" />
        <meta property="og:description" content="Calculate your Survey Response Rate instantly. Get actionable insights to improve survey completion rates and gather better feedback." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Survey Response Rate Calculator: Boost Survey Performance" />
        <meta property="twitter:description" content="Calculate your Survey Response Rate instantly. Get actionable insights to improve survey completion rates and gather better feedback." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Customer Survey Response Rate Calculator",
            "description": "Free calculator to measure and optimize survey response rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Survey Response Rate",
              "Industry Benchmarks",
              "Survey Optimization Tips",
              "Response Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Survey Response Rate",
              "description": "The percentage of people who complete a survey compared to the total number of surveys distributed."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Market Researchers, Customer Experience Managers, Business Analysts"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Survey Response Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter number of surveys sent"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of surveys completed"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your response rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Customer Survey Response Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Surveys Sent</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={surveysSent}
                  onChange={(e) => setSurveysSent(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Surveys Completed</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CheckSquare className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={surveysCompleted}
                  onChange={(e) => setSurveysCompleted(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="150"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {responseRate}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Survey Response Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your customer survey response rate is {responseRate}%. This means that {responseRate}% of customers who received your survey completed it.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Survey Type Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Customer Satisfaction: 15-25%</li>
          <li>Post-Purchase Feedback: 20-30%</li>
          <li>Employee Surveys: 30-40%</li>
          <li>Market Research: 10-15%</li>
          <li>Email Surveys: 20-30%</li>
          <li>In-App Surveys: 30-40%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Response Rate Optimization</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Keep surveys short and focused</li>
          <li>Communicate survey purpose clearly</li>
          <li>Personalize survey invitations</li>
          <li>Offer completion incentives</li>
          <li>Send strategic reminders</li>
          <li>Optimize for mobile devices</li>
          <li>Time surveys appropriately</li>
          <li>Use engaging question types</li>
          <li>Ensure survey relevance</li>
          <li>Follow up with respondents</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Survey length: 45% impact on completion</li>
          <li>Mobile optimization: 35% higher response rates</li>
          <li>Personalization: 25% increase in responses</li>
          <li>Incentives: 30% boost in completion rates</li>
          <li>Timing: 20% impact on response rates</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Best Practices</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Limit surveys to 5-10 questions</li>
          <li>Send within 24 hours of interaction</li>
          <li>Use a mix of question types</li>
          <li>Include progress indicators</li>
          <li>Test surveys before sending</li>
          <li>Analyze partial responses</li>
          <li>Monitor completion time</li>
        </ul>
      </div>
    </div>
  );
};

export default CustomerSurveyResponseRateCalculator;
